export const tagSizes = ["lg", "md", "sm", "xs"] as const;

export type TagSize = (typeof tagSizes)[number];

export const tagColors = ["accent", "primary", "neutral", "error"] as const;

export type TagColor = (typeof tagColors)[number];

export interface TagSandboxState {
    size: TagSize;
    color: TagColor;
    isOutline: boolean;
    isNumeric: boolean;
    hasLeftIcon: boolean;
    isRemovable: boolean;
    isClickable: boolean;
}

export const defaultTagSandboxState: TagSandboxState = {
    size: "md",
    color: "accent",
    isOutline: false,
    isNumeric: false,
    hasLeftIcon: false,
    isRemovable: false,
    isClickable: false,
};

export const canUseColor = (size: TagSize, color: TagColor): boolean =>
    size !== "lg" || color === "primary" || color === "accent";

const isRemovableSize = (size: TagSize): size is "md" | "lg" =>
    size === "md" || size === "lg";

/**
 * Coerce sandbox flags into a combination the Tag union accepts, so toggling
 * one control never produces an illegal prop set.
 */
export const legalizeTagSandbox = (next: TagSandboxState): TagSandboxState => {
    const size = next.size;
    const isOutline = next.isOutline;
    const isClickable = next.isClickable;
    let color = next.color;
    let isNumeric = next.isNumeric;
    let hasLeftIcon = next.hasLeftIcon;
    let isRemovable = next.isRemovable;

    if (!canUseColor(size, color)) {
        color = "primary";
    }

    if (size === "xs") {
        isNumeric = true;
        hasLeftIcon = false;
        isRemovable = false;
    }

    if (size === "sm") {
        isRemovable = false;
    }

    if (isNumeric) {
        hasLeftIcon = false;
        isRemovable = false;
        if (size === "lg") {
            color = "primary";
        }
    }

    if (!isRemovableSize(size)) {
        isRemovable = false;
    }

    return {
        size,
        color,
        isOutline,
        isNumeric,
        hasLeftIcon,
        isRemovable,
        isClickable,
    };
};

export const canUseNumeric = (size: TagSize): boolean => size !== "xs";

export const canUseLeftIcon = ({
    size,
    isNumeric,
}: Pick<TagSandboxState, "size" | "isNumeric">): boolean =>
    size !== "xs" && !isNumeric;

export const canUseRemovable = ({
    size,
    isNumeric,
}: Pick<TagSandboxState, "size" | "isNumeric">): boolean =>
    isRemovableSize(size) && !isNumeric;
